import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProductOptionalFieldNameEnum } from '@inter-ikea-kompis/enums';
import { getKompisSettings } from '@inter-ikea-storageone/shared/services/kompisSettings';
import type { Range } from '@inter-ikea-storageone/shared/types';
import { getDexfSettings } from '@inter-ikea-storageone/shared/services/dexfSettingsService';
import { getPhraseTranslations } from '@inter-ikea-storageone/shared/services/phraseTranslationService';
import { getRangeName } from '@inter-ikea-storageone/shared/utils/rangeName';
import { KompisDefaultFields } from '@inter-ikea-kompis/services';
import { getCountry, getCustomLocale } from '@inter-ikea-storageone/shared/utils/i18nUtils';
import FallbackScreen from '@app/components/FallbackScreen/FallbackScreen';
import type {
  IStorageOneDexfSettings,
  IStorageOneTranslations,
  KompisSettings
} from '@inter-ikea-storageone/shared/types';
import { e2e, getRange } from '@inter-ikea-storageone/shared/utils/queryUtils';
import { ShoppingSupport } from '@inter-ikea-kompis/utilities';

interface KompisServices {
  shoppingListSupported: boolean;
  shoppingCartSupported: boolean;
}

type ServiceSettingsContextValue = {
  range: Range;
  rangeName: string;
  kompisSettings: KompisSettings;
  dexfSettings: IStorageOneDexfSettings;
  productFields: ProductOptionalFieldNameEnum[];
  translations: IStorageOneTranslations;
  services: KompisServices;
  dexfEndpoints: {
    baseUrl: string;
    itemList: string;
  };
};

const productFields: ProductOptionalFieldNameEnum[] = [
  ...KompisDefaultFields,
  ProductOptionalFieldNameEnum.filterAttribute
];

const ServiceSettingsContext = createContext<ServiceSettingsContextValue>({
  range: 'pax' as Range,
  rangeName: 'PAX',
  kompisSettings: getKompisSettings({} as IStorageOneDexfSettings),
  services: {
    shoppingListSupported: true,
    shoppingCartSupported: true
  },
  productFields: productFields,
  dexfEndpoints: { baseUrl: '', itemList: '' },
  translations: {} as IStorageOneTranslations,
  dexfSettings: {
    kompis: {},
    localisation: {
      writeDirection: 'ltr' as const
    }
  } as IStorageOneDexfSettings
});

export const useServiceSettings = () => useContext(ServiceSettingsContext);

export const getAllSettings = async (langCode?: Lowercase<string>) => {
  const locale = getCustomLocale(langCode);

  const [dexfSettings, translations] = await Promise.all([
    getDexfSettings(langCode),
    getPhraseTranslations(locale)
  ]);

  const range = getRange();
  const countryCode = getCountry(locale);
  const rangeName = getRangeName(range);
  const shoppingCartSupported = ShoppingSupport.hasCartSupport(dexfSettings);
  const shoppingListSupported = ShoppingSupport.hasListSupport(dexfSettings);

  return {
    range,
    rangeName,
    kompisSettings: getKompisSettings(dexfSettings),
    services: {
      shoppingListSupported,
      shoppingCartSupported
    },
    dexfEndpoints: {
      baseUrl: 'https://api.dexf.ikea.com/',
      itemList: `https://api.dexf.ikea.com/vpc/v1/itemlist/retailunit/${countryCode}/locale/${locale}/`
    },
    productFields,
    dexfSettings,
    translations
  };
};

export const ServiceSettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['serviceSettings'],
    queryFn: () => getAllSettings()
  });

  if (isLoading) return null;

  if (error) {
    return (
      <FallbackScreen
        title={({ formatMessage }) =>
          formatMessage({ id: 'storageone.fallbackPlannerUnavailableTitle' })
        }
        body={({ formatMessage }) =>
          e2e() ? error.message : formatMessage({ id: 'storageone.fallbackPlannerUnavailableBody' })
        }
        button={({ formatMessage }) =>
          formatMessage({ id: 'storageone.fallbackPlannerUnavailableButton' })
        }
        onClick={() => {
          window.history.back();
        }}
      />
    );
  }

  if (!data) return null;

  return <ServiceSettingsContext.Provider value={data}>{children}</ServiceSettingsContext.Provider>;
};
